// src/pages/Orders.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  CssBaseline,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme, alpha } from '@mui/material/styles';
import { Download, FileText, Mail, MapPin, PackageSearch, Receipt, Truck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TaxDetailsDialog from '../components/TaxDetailsDialog';
import { BrandSpinner } from '../components/Loading';
import { auth } from '../firebase';
import { getOrdersForUser } from '../db';
import { requestInvoice, resendConfirmation, saveBlob } from '../orderApi';
import { orderLines } from '../catalogue';
import { discountRule, priceLine } from '../pricing';
import { useSeo } from '../seo';

const money = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const rand = (value) => `R${money.format(Number(value) || 0)}`;

/** Firestore timestamps arrive in several shapes; normalise them all to Date. */
const toDate = (value) => {
  if (!value) return null;
  try {
    let date;
    if (typeof value.toDate === 'function') date = value.toDate();
    else if (value.seconds) date = new Date(value.seconds * 1000);
    else date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  } catch (err) {
    return null;
  }
};

const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Africa/Johannesburg',
  }).format(date);
};

/**
 * The order's state in the customer's own terms.
 *
 * The dashboard's vocabulary is about the work — "to fulfil", "fulfilled" —
 * which is not what someone waiting for hay wants to read. Same flags, said
 * from the other side of the counter.
 */
const describeForCustomer = (order) => {
  if (order.status === 'amount_mismatch') {
    return {
      label: 'Needs attention',
      color: 'error',
      note: 'We received your payment but the amount did not match the order. Our team will be in touch.',
    };
  }
  if (order.fulfilled) {
    return { label: 'Completed', color: 'success', note: 'This order has been dispatched.' };
  }
  return {
    label: 'Being prepared',
    color: 'info',
    note: 'Your order is paid for and is being prepared for dispatch.',
  };
};

/** The bought lines, named and priced the way they were sold. */
const linesOf = (order) =>
  orderLines(order).map((line) => {
    const priced = priceLine(line.qty, line.price, discountRule(order, line.productId));
    return { id: line.productId, name: line.name, ...priced };
  });

/**
 * What the order is actually worth. A total written by hand wins outright —
 * including R0, which is a comped order and a deliberate answer — otherwise the
 * stored total, and only then the sum of the parts.
 */
const totalOf = (order) => {
  const stored = Number(order.total);
  const overridden =
    order.totalOverridden === true && order.total !== '' && Number.isFinite(stored);
  if (overridden) return Math.max(0, stored);
  if (Number.isFinite(stored) && stored > 0) return stored;
  const subtotal = linesOf(order).reduce((sum, line) => sum + line.gross, 0);
  return subtotal - (Number(order.discountAmount) || 0) + (Number(order.deliveryFee) || 0);
};

/**
 * The page frame. Declared at module scope on purpose: nested inside the
 * component it would be a new component type on every render, and React would
 * throw the Header away and rebuild it — losing its auth state each keystroke.
 */
const Shell = ({ children }) => (
  <>
    <CssBaseline />
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />
      {children}
      <Footer />
    </Box>
  </>
);

/** A label above a value, used for the small facts under an order. */
const Fact = ({ icon, label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.disabled', mb: 0.5 }}>
      {icon}
      <Typography
        sx={{
          fontSize: '0.66rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
    </Stack>
    <Typography sx={{ fontSize: '0.88rem', color: 'text.primary', lineHeight: 1.6 }}>
      {children}
    </Typography>
  </Box>
);

const Orders = () => {
  useSeo({ title: 'Your orders | Crestancia', path: '/orders', noindex: true });

  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;

  const [user, authLoading] = useAuthState(auth);
  const [orders, setOrders] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [invoiceFor, setInvoiceFor] = useState(null);
  // Which order has a request in flight, and which one — so only the button
  // that was pressed shows that it is working.
  const [busy, setBusy] = useState({ id: null, action: null });

  const load = useCallback(async () => {
    if (!user) return;
    try {
      setLoadError('');
      const found = await getOrdersForUser(user);
      setOrders(found);
    } catch (err) {
      console.error('Error loading orders', err);
      setOrders([]);
      setLoadError('We could not load your orders just now. Please refresh and try again.');
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setOrders([]);
      return;
    }
    load();
  }, [authLoading, user, load]);

  /**
   * The details to prefill the dialog with: this order's own if it has been
   * invoiced before, otherwise the last set the customer gave on any order, so
   * a repeat buyer types their VAT number once.
   */
  const detailsFor = useCallback(
    (order) => {
      const own = order?.invoice?.taxDetails;
      if (own && Object.keys(own).length > 0) return own;
      const previous = (orders || []).find(
        (candidate) =>
          candidate.invoice?.taxDetails &&
          Object.keys(candidate.invoice.taxDetails).length > 0
      );
      return previous?.invoice?.taxDetails || null;
    },
    [orders]
  );

  const handleInvoice = async ({ taxDetails, sendEmail }) => {
    const order = invoiceFor;
    if (!order) return;
    setBusy({ id: order.id, action: 'invoice' });
    try {
      const result = await requestInvoice(order.id, { taxDetails, sendEmail });
      saveBlob(result.blob, result.filename);
      toast.success(
        result.emailed
          ? `${result.documentTitle} ${result.number} downloaded and emailed to you.`
          : `${result.documentTitle} ${result.number} downloaded.`
      );
      setInvoiceFor(null);
      // The order now carries an invoice number; show it without a full reload.
      setOrders((current) =>
        (current || []).map((candidate) =>
          candidate.id === order.id
            ? {
                ...candidate,
                invoice: {
                  ...(candidate.invoice || {}),
                  number: result.number,
                  issuedAt: result.issuedAt,
                  taxDetails: taxDetails || candidate.invoice?.taxDetails || {},
                },
              }
            : candidate
        )
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy({ id: null, action: null });
    }
  };

  const handleResend = async (order) => {
    setBusy({ id: order.id, action: 'email' });
    try {
      const result = await resendConfirmation(order.id);
      toast.success(result.message || 'Confirmation email sent.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy({ id: null, action: null });
    }
  };

  const summary = useMemo(() => {
    const list = orders || [];
    return {
      count: list.length,
      spend: list.reduce((sum, order) => sum + totalOf(order), 0),
      last: list.length ? formatDate(list[0].created_at) : '—',
    };
  }, [orders]);

  const loading = authLoading || orders === null;

  return (
    <Shell>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: dark
            ? 'linear-gradient(180deg, rgba(85,107,47,0.16) 0%, rgba(85,107,47,0) 100%)'
            : 'linear-gradient(180deg, rgba(85,107,47,0.09) 0%, rgba(85,107,47,0) 100%)',
        }}
      >
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: accent, mb: 1 }}>
            <Receipt size={17} strokeWidth={2} />
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
              }}
            >
              Your account
            </Typography>
          </Stack>
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
            Your orders
          </Typography>
          <Typography sx={{ color: 'text.secondary', mt: 1, maxWidth: 620, lineHeight: 1.7 }}>
            Everything you have bought from us, with a tax invoice you can download whenever you
            need one and a confirmation email we can send again.
          </Typography>

          {!loading && summary.count > 0 && (
            <Stack
              direction="row"
              spacing={{ xs: 3, sm: 5 }}
              sx={{ mt: 3.5, flexWrap: 'wrap', rowGap: 2 }}
            >
              {[
                { label: 'Orders', value: String(summary.count) },
                { label: 'Total spent', value: rand(summary.spend) },
                { label: 'Most recent', value: summary.last },
              ].map((stat) => (
                <Box key={stat.label}>
                  <Typography
                    sx={{
                      fontSize: '0.66rem',
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', mt: 0.25 }}>
                    {stat.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ flexGrow: 1, py: { xs: 3, md: 5 } }}>
        {loadError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {loadError}
          </Alert>
        )}

        {loading && (
          <Stack spacing={2}>
            {[0, 1, 2].map((key) => (
              <Skeleton key={key} variant="rounded" height={132} sx={{ borderRadius: 3 }} />
            ))}
          </Stack>
        )}

        {!loading && orders.length === 0 && !loadError && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, color: 'text.disabled' }}>
              <PackageSearch size={44} strokeWidth={1.5} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              No orders yet
            </Typography>
            <Typography sx={{ color: 'text.secondary', mt: 1, mb: 3, maxWidth: 440, mx: 'auto' }}>
              Once you have placed an order it will appear here, along with its invoice. If you
              ordered over the phone, ask us to link it to this email address.
            </Typography>
            <Button
              component={RouterLink}
              to="/home#products"
              variant="contained"
              disableElevation
              sx={{ borderRadius: 999, px: 4, py: 1.3, fontWeight: 700, textTransform: 'none' }}
            >
              Browse our products
            </Button>
          </Paper>
        )}

        <Stack spacing={2.5}>
          {!loading &&
            orders.map((order) => {
              const status = describeForCustomer(order);
              const lines = linesOf(order);
              const total = totalOf(order);
              const itemCount = lines.reduce((sum, line) => sum + line.qty, 0);
              const open = expandedId === order.id;
              const working = busy.id === order.id;
              const invoiceNumber = order.invoice?.number || '';
              const subtotal = lines.reduce((sum, line) => sum + line.gross, 0);
              const discount = Number(order.discountAmount) || 0;
              const delivery = Number(order.deliveryFee) || 0;
              const adjustment = total - (subtotal - discount + delivery);

              return (
                <Paper
                  key={order.id}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: open ? alpha(accent, 0.45) : 'divider',
                    overflow: 'hidden',
                    transition: 'border-color .2s ease, box-shadow .2s ease',
                    '&:hover': { boxShadow: dark ? '0 6px 22px rgba(0,0,0,.34)' : '0 6px 22px rgba(60,54,40,.08)' },
                  }}
                >
                  <Box sx={{ p: { xs: 2.25, sm: 3 } }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1.5}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                            {order.orderNum || 'Order'}
                          </Typography>
                          <Chip label={status.label} color={status.color} size="small" />
                          {invoiceNumber && (
                            <Chip
                              label={invoiceNumber}
                              size="small"
                              variant="outlined"
                              icon={<FileText size={13} />}
                              sx={{ '& .MuiChip-icon': { ml: 0.75 } }}
                            />
                          )}
                        </Stack>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>
                          {formatDate(order.created_at)} · {itemCount} item
                          {itemCount === 1 ? '' : 's'}
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
                        <Typography
                          sx={{
                            fontSize: '0.66rem',
                            fontWeight: 700,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            color: 'text.disabled',
                          }}
                        >
                          Total paid
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.4rem', lineHeight: 1.2 }}>
                          {rand(total)}
                        </Typography>
                      </Box>
                    </Stack>

                    {order.status === 'amount_mismatch' && (
                      <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                        {status.note}
                      </Alert>
                    )}

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 2.25, flexWrap: 'wrap', rowGap: 1 }}
                    >
                      <Button
                        onClick={() => setInvoiceFor(order)}
                        variant="contained"
                        disableElevation
                        size="small"
                        disabled={working}
                        startIcon={
                          working && busy.action === 'invoice' ? (
                            <BrandSpinner size={16} />
                          ) : invoiceNumber ? (
                            <Download size={15} />
                          ) : (
                            <FileText size={15} />
                          )
                        }
                        sx={{ borderRadius: 999, px: 2.25, fontWeight: 700, textTransform: 'none' }}
                      >
                        {invoiceNumber ? 'Download invoice' : 'Request tax invoice'}
                      </Button>

                      <Button
                        onClick={() => handleResend(order)}
                        size="small"
                        disabled={working}
                        startIcon={<Mail size={15} />}
                        sx={{
                          borderRadius: 999,
                          px: 2,
                          fontWeight: 600,
                          textTransform: 'none',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {working && busy.action === 'email' ? 'Sending…' : 'Email confirmation'}
                      </Button>

                      <Box sx={{ flexGrow: 1 }} />

                      <Button
                        onClick={() => setExpandedId(open ? null : order.id)}
                        size="small"
                        endIcon={
                          <ExpandMoreIcon
                            sx={{
                              transform: open ? 'rotate(180deg)' : 'none',
                              transition: 'transform .2s ease',
                            }}
                          />
                        }
                        sx={{ fontWeight: 600, textTransform: 'none', color: 'text.secondary' }}
                      >
                        {open ? 'Hide details' : 'View details'}
                      </Button>
                    </Stack>
                  </Box>

                  <Collapse in={open} unmountOnExit>
                    <Divider />
                    <Box
                      sx={{
                        p: { xs: 2.25, sm: 3 },
                        backgroundColor: dark
                          ? 'rgba(236,231,219,0.03)'
                          : 'rgba(85,107,47,0.028)',
                      }}
                    >
                      <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Qty
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Unit price
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Amount
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {lines.map((line) => (
                              <TableRow key={line.id}>
                                <TableCell>
                                  {line.name}
                                  {line.discount > 0 && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        display: 'block',
                                        fontSize: '0.74rem',
                                        color: accent,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      {rand(line.discount)} off
                                      {line.capReached ? ` (first ${line.discountedUnits})` : ''}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">{line.qty}</TableCell>
                                <TableCell align="right">{rand(line.unitPrice)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                  {rand(line.gross)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Stack sx={{ mt: 2, ml: 'auto', maxWidth: 280 }} spacing={0.75}>
                        {[
                          { name: 'Subtotal', value: rand(subtotal) },
                          ...(discount > 0
                            ? [
                                {
                                  name: order.discountCode
                                    ? `Discount (${order.discountCode})`
                                    : 'Discount',
                                  value: `-${rand(discount)}`,
                                },
                              ]
                            : []),
                          ...(delivery > 0 ? [{ name: 'Delivery', value: rand(delivery) }] : []),
                          ...(Math.abs(adjustment) >= 0.005
                            ? [
                                {
                                  name: 'Adjustment',
                                  value: `${adjustment < 0 ? '-' : ''}${rand(Math.abs(adjustment))}`,
                                },
                              ]
                            : []),
                        ].map((row) => (
                          <Stack key={row.name} direction="row" justifyContent="space-between">
                            <Typography sx={{ color: 'text.secondary', fontSize: '0.86rem' }}>
                              {row.name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.86rem' }}>{row.value}</Typography>
                          </Stack>
                        ))}
                        <Divider sx={{ my: 0.75 }} />
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                          <Typography sx={{ fontWeight: 700 }}>{rand(total)}</Typography>
                        </Stack>
                      </Stack>

                      <Divider sx={{ my: 2.5 }} />

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 2, sm: 4 }}
                        sx={{ flexWrap: 'wrap', rowGap: 2 }}
                      >
                        <Fact icon={<MapPin size={13} />} label="Delivered to">
                          {order.customer_name}
                          <br />
                          {order.customer_address}
                          <br />
                          {[order.customer_city, order.customer_postal_code]
                            .filter(Boolean)
                            .join(', ')}
                          {order.customer_phone && (
                            <>
                              <br />
                              {order.customer_phone}
                            </>
                          )}
                        </Fact>
                        <Fact icon={<Truck size={13} />} label="Status">
                          {status.note}
                        </Fact>
                        <Fact icon={<Mail size={13} />} label="Confirmation sent to">
                          {order.customer_email}
                        </Fact>
                      </Stack>
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
        </Stack>

        {!loading && orders.length > 0 && (
          <Typography
            sx={{ mt: 4, color: 'text.secondary', fontSize: '0.82rem', lineHeight: 1.7 }}
          >
            Placed an order over the phone and cannot see it here? It will appear once your email
            address has been confirmed on this account and linked to that order — get in touch and
            we will sort it out.
          </Typography>
        )}
      </Container>

      <TaxDetailsDialog
        open={Boolean(invoiceFor)}
        order={invoiceFor}
        initialDetails={invoiceFor ? detailsFor(invoiceFor) : null}
        alreadyIssued={invoiceFor?.invoice?.number || ''}
        busy={busy.action === 'invoice'}
        onClose={() => setInvoiceFor(null)}
        onSubmit={handleInvoice}
      />
    </Shell>
  );
};

export default Orders;
